import { PAF_RATE } from "@/constants/maoCalculator";

import { MAX_PAYMENT_WINDOW_DAYS, MIN_PAYMENT_WINDOW_DAYS, PAYMENT_WINDOW_DAYS } from "./pafConstants";

export { PAF_RATE };

export const PAF_MIN_FEE = 500;
export const PAF_TIER_STEP = 500;
export const PAF_TIER_WIDTH = 100000;
export const PAF_MAX_MAO = 1000000000;

/**
 * Sanitizes MAO input before fee tiering.
 *
 * @param {*} rawMao - Candidate MAO value from calculator/deal payload.
 * @returns {number} Safe non-negative MAO number bounded for tier math.
 */
export function sanitizeMaoForPaf(rawMao) {
  const parsed = Number(rawMao);
  if (!Number.isFinite(parsed)) return 0;
  if (parsed <= 0) return 0;
  return Math.min(parsed, PAF_MAX_MAO);
}

/**
 * Computes fee from MAO using flat tiers:
 * 0-99,999 => 500, 100,000-199,999 => 1,000, etc.
 *
 * @param {*} rawMao - Candidate MAO value.
 * @returns {number} Tiered PAF fee amount.
 */
export function calculatePafAmountFromMao(rawMao) {
  const mao = sanitizeMaoForPaf(rawMao);
  const tierIndex = Math.floor(mao / PAF_TIER_WIDTH);
  return PAF_MIN_FEE + tierIndex * PAF_TIER_STEP;
}

/**
 * Ensures amount sent to backend is a valid tier fee and not tampered.
 * Falls back to recomputed amount from MAO if invalid/mismatched.
 *
 * @param {*} candidateAmount - Amount captured from UI at acceptance time.
 * @param {*} rawMao - Locked MAO used for independent recomputation.
 * @returns {number} Safe fee amount.
 */
export function sanitizeAcceptedPafAmount(candidateAmount, rawMao) {
  const computed = calculatePafAmountFromMao(rawMao);
  const parsed = Number(candidateAmount);
  if (!Number.isFinite(parsed)) return computed;
  const normalized = Math.round(parsed);
  if (normalized < PAF_MIN_FEE) return computed;
  if ((normalized - PAF_MIN_FEE) % PAF_TIER_STEP !== 0) return computed;
  if (normalized !== computed) return computed;
  return normalized;
}

/**
 * Normalizes payment window days to a strict integer range.
 *
 * @param {*} rawDays - Candidate day count.
 * @returns {number} Safe integer day count.
 */
export function sanitizePaymentWindowDays(rawDays) {
  const parsed = Number(rawDays);
  if (!Number.isFinite(parsed)) return PAYMENT_WINDOW_DAYS;
  const normalized = Math.round(parsed);
  if (normalized < MIN_PAYMENT_WINDOW_DAYS || normalized > MAX_PAYMENT_WINDOW_DAYS) {
    return PAYMENT_WINDOW_DAYS;
  }
  return normalized;
}
